import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import InputField from "../../../../Components/Masters/InputField";
import SelectField from "../../../../Components/Masters/SelectField";
import AutoComplete from "../../../../Components/Common/AutoComplete";
import contries from "../../Roles and Permissions/Country.json";
import EditDialog from "../../../../Components/Common/EditDialog";
import { get, post, put } from "../../../../utils/api";

const ASSET_SIZE_UNITS = ["sq m", "sq ft"];
const OTHER_ASSET_TYPE = "other";

function toDateInputValue(val) {
  if (!val) return "";
  const s = String(val);
  if (s.length >= 10 && s[4] === "-") return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/** YYYY-MM-DD + days in local calendar (avoids UTC parse issues). */
function addDaysIsoDate(isoDateStr, days) {
  if (!isoDateStr || String(isoDateStr).length < 10) return "";
  const y = Number(isoDateStr.slice(0, 4));
  const m = Number(isoDateStr.slice(5, 7)) - 1;
  const d = Number(isoDateStr.slice(8, 10));
  const dt = new Date(y, m, d);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + days);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function AddLeasedAssets({
  formInputData,
  fetchData,
  id,
  element,
  data,
  setAddData,
  addData,
  setsnackOpen,
  addUrl,
  updateUrl,
  leasedType,
  drawerZIndex,
  embedded = false,
  embeddedTitle,
  /** When true, lease end must be strictly after start (same calendar day not allowed). */
  leaseEndStrictlyAfterStart = false,
  /** When true, Asset ID input is read-only/disabled. */
  assetIdReadOnly = false,
}) {
  const [errors, setErrors] = useState({});
  const [leasedAssetTypeNames, setLeasedAssetTypeNames] = useState([]);
  const [showAddAssetTypeForm, setShowAddAssetTypeForm] = useState(false);
  const [newLeasedAssetType, setNewLeasedAssetType] = useState("");

  const buildInitialState = () => ({
    assetId: element?.assetId || "",
    locationCity: element?.locationCity || "",
    locationCountry: element?.locationCountry || "",
    typeOfAsset: element?.typeOfAsset || "",
    leaseStartDate: toDateInputValue(element?.leaseStartDate),
    leaseEndDate: toDateInputValue(element?.leaseEndDate),
    assetSize:
      element?.assetSize !== undefined && element?.assetSize !== null
        ? String(element.assetSize)
        : "",
    assetSizeUnit: element?.assetSizeUnit || "",
  });

  const [formData, setFormData] = useState(buildInitialState);
  const [editDialog, setEditDialog] = useState(false);
  const [formData2, setFormData2] = useState(buildInitialState);

  useEffect(() => {
    const next = buildInitialState();
    setFormData(next);
    setFormData2(next);
    setShowAddAssetTypeForm(false);
    setNewLeasedAssetType("");
  }, [element, id]);

  const fetchLeasedAssetTypes = async () => {
    try {
      const res = await get(`/master/getAllLeasedAssetType?leasedType=${leasedType}`);
      const list = res?.data?.data || [];
      const names = list
        .filter((item) => item.active !== false)
        .filter((item) =>
          Array.isArray(item.leasedType)
            ? item.leasedType.includes(leasedType)
            : item.leasedType === leasedType
        )
        .map((item) => item.name)
        .filter(Boolean);
      setLeasedAssetTypeNames([...new Set(names)].sort((a, b) => a.localeCompare(b)));
    } catch (e) {
      console.error("Error fetching leased asset types:", e);
    }
  };

  useEffect(() => {
    if (!addData) return;
    fetchLeasedAssetTypes();
  }, [addData, leasedType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "assetSize" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (
        leaseEndStrictlyAfterStart &&
        name === "leaseStartDate" &&
        value &&
        prev.leaseEndDate &&
        prev.leaseEndDate <= value
      ) {
        next.leaseEndDate = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "typeOfAsset") {
      if (value === OTHER_ASSET_TYPE) {
        setShowAddAssetTypeForm(true);
      } else {
        setShowAddAssetTypeForm(false);
        setNewLeasedAssetType("");
      }
    }

    if (name === "assetId") {
      if (assetIdReadOnly) return;
      const dup = data?.filter(
        (el) => el.assetId?.toLowerCase() === value?.toLowerCase()
      );
      if (
        dup?.length > 0 &&
        (element ? element._id !== dup[0]._id : true)
      ) {
        setErrors((prev) => ({
          ...prev,
          assetId: "Asset ID already exists.",
        }));
      }
    }
  };

  const handleCountryChange = (event) => {
    const selectedCountryCode = event.target.value;
    const selectedCountry = contries.countries.find(
      (country) => country.name === selectedCountryCode
    );
    setErrors((prev) => ({ ...prev, locationCountry: "" }));
    if (selectedCountry) {
      setFormData((prev) => ({
        ...prev,
        locationCountry: selectedCountry.name,
      }));
    }
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    const cleared = buildInitialState();
    setFormData(cleared);
    setFormData2(cleared);
    setShowAddAssetTypeForm(false);
    setNewLeasedAssetType("");
  };

  const handleAddLeasedAssetType = async () => {
    const name = newLeasedAssetType.trim();
    if (!name) return;
    try {
      const response = await post("/master/addLeasedAssetType", {
        name,
        leasedType,
      });
      const createdName = response?.data?.data?.name ?? name;
      setFormData((prev) => ({ ...prev, typeOfAsset: createdName }));
      setNewLeasedAssetType("");
      setShowAddAssetTypeForm(false);
      await fetchLeasedAssetTypes();
    } catch (error) {
      setsnackOpen({
        open: true,
        message: "This asset type already exists or could not be created",
        severity: "error",
      });
      setNewLeasedAssetType("");
      setShowAddAssetTypeForm(false);
    }
  };

  const handleClose = () => {
    const unchanged = Object.keys(formData).every(
      (key) => formData[key] === formData2[key]
    );
    if (unchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateForm = () => {
    const next = { ...errors };
    formInputData.forEach((input) => {
      const value = formData[input.fieldName];
      if (input.fieldName === "assetSize") {
        const n = Number(value);
        if (value === "" || value === undefined)
          next.assetSize = `${input.title} is required`;
        else if (Number.isNaN(n) || n <= 0)
          next.assetSize = "Enter a valid positive number";
        else delete next.assetSize;
      } else if (
        input.fieldName === "typeOfAsset" &&
        (!value || value === OTHER_ASSET_TYPE || String(value).trim() === "")
      ) {
        next.typeOfAsset = `${input.title} is required`;
      } else if (!value || String(value).trim() === "") {
        next[input.fieldName] = `${input.title} is required`;
      } else {
        delete next[input.fieldName];
      }
    });

    if (formData.leaseStartDate && formData.leaseEndDate) {
      if (leaseEndStrictlyAfterStart) {
        if (formData.leaseEndDate <= formData.leaseStartDate) {
          next.leaseEndDate = "Lease end date must be after start date";
        }
      } else if (formData.leaseEndDate < formData.leaseStartDate) {
        next.leaseEndDate = "Lease end date must be on or after start date";
      }
    }

    setErrors(next);
    return !Object.keys(next).some((k) => next[k]);
  };

  const Save = async () => {
    if (!validateForm()) return;
    const method = id ? put : post;
    const url = id ? `${updateUrl}?id=${id}` : addUrl;
    const payload = {
      assetId: formData.assetId.trim(),
      locationCity: formData.locationCity.trim(),
      locationCountry: formData.locationCountry,
      typeOfAsset: formData.typeOfAsset.trim(),
      leaseStartDate: formData.leaseStartDate,
      leaseEndDate: formData.leaseEndDate,
      assetSize: Number(formData.assetSize),
      assetSizeUnit: formData.assetSizeUnit,
      leasedType,
    };

    await method(url, payload)
      .then((res) => {
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchData();
        handleExitAgree();
      })
      .catch((err) => {
        if (err.response?.data?.errors) {
          const errorMessages = err.response.data.errors
            .map((error) => error.msg)
            .join(", ");
          setsnackOpen({
            open: true,
            message: errorMessages,
            severity: "error",
          });
        }
      });
  };

  const renderField = (el) => {
    if (el.fieldName === "leaseStartDate" || el.fieldName === "leaseEndDate") {
      return (
        <Box key={el.fieldName}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
            {el.title}
          </Typography>
          <TextField
            type="date"
            name={el.fieldName}
            size="small"
            fullWidth
            value={formData[el.fieldName] || ""}
            onChange={handleChange}
            inputProps={{
              min:
                el.fieldName === "leaseEndDate"
                  ? leaseEndStrictlyAfterStart && formData.leaseStartDate
                    ? addDaysIsoDate(formData.leaseStartDate, 1)
                    : formData.leaseStartDate || undefined
                  : undefined,
            }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors[el.fieldName])}
          />
          <Typography variant="body2" color="#D34040">
            {errors[el.fieldName] || ""}
          </Typography>
        </Box>
      );
    }

    if (el.fieldName === "locationCountry") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.locationCountry}
          handleChange={handleCountryChange}
          options={contries.countries}
          errors={errors[el.fieldName]}
        />
      );
    }

    if (el.fieldName === "assetSizeUnit") {
      return null;
    }

    if (el.fieldName === "assetSize") {
      return (
        <Box key={el.fieldName}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
            {el.title}
          </Typography>
          <Box display="flex" gap={1} sx={{ width: "100%" }}>
            <Box sx={{ width: "46%" }}>
              <TextField
                size="small"
                fullWidth
                name="assetSize"
                type="text"
                placeholder="Enter no"
                value={formData.assetSize || ""}
                onChange={handleChange}
                error={Boolean(errors.assetSize)}
                helperText={errors.assetSize || ""}
              />
            </Box>
            <Box sx={{ width: "54%" }}>
              <TextField
                select
                size="small"
                fullWidth
                name="assetSizeUnit"
                value={formData.assetSizeUnit || ""}
                onChange={handleChange}
                error={Boolean(errors.assetSizeUnit)}
              >
                <MenuItem value="" disabled>
                  Select unit
                </MenuItem>
                {ASSET_SIZE_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" color="#D34040">
                {errors.assetSizeUnit || ""}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (el.fieldName === "typeOfAsset") {
      const current = formData.typeOfAsset;
      const optionNames = [...leasedAssetTypeNames, OTHER_ASSET_TYPE];
      if (
        current &&
        current !== OTHER_ASSET_TYPE &&
        !optionNames.includes(current)
      ) {
        optionNames.push(current);
        optionNames.sort((a, b) => a.localeCompare(b));
      }
      return (
        <Box key={el.fieldName}>
          <AutoComplete
            title={el.title}
            name={el.fieldName}
            handleChange={handleChange}
            value={formData[el.fieldName]}
            options={optionNames}
            errors={errors[el.fieldName]}
            disableTyping={true}
          />
          {showAddAssetTypeForm && (
            <Box sx={{ mt: 1 }}>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Enter new asset type name"
                value={newLeasedAssetType}
                onChange={(e) => setNewLeasedAssetType(e.target.value)}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#0a3f73",
                  textTransform: "none",
                  marginTop: "3%",
                }}
                onClick={handleAddLeasedAssetType}
              >
                Add
              </Button>
            </Box>
          )}
          <Typography variant="body2" color="#D34040">
            {errors[el.fieldName] || ""}
          </Typography>
        </Box>
      );
    }

    return (
      <InputField
        key={el.fieldName}
        title={el.title}
        handleChange={handleChange}
        placeholder={el.placeholder}
        value={formData[el.fieldName] || ""}
        errors={errors[el.fieldName]}
        fieldName={el.fieldName}
        disabled={el.fieldName === "assetId" && assetIdReadOnly}
      />
    );
  };

  const list = () => (
    <Box
      display="flex"
      gap={3}
      sx={{ width: embedded ? "100%" : "28rem", maxWidth: "100%" }}
    >
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      {!embedded && (
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          width: embedded ? "100%" : "90%",
          p: 3,
          backgroundColor: "#fff",
          height: embedded ? "auto" : "100vh",
          overflowY: embedded ? "visible" : "scroll",
        }}
      >
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
        >
          {embeddedTitle || (id ? "Edit Leased Asset" : "Add Leased Asset")}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Module:{" "}
          <strong>
            {leasedType === "upstream" ? "Upstream" : "Downstream"}
          </strong>{" "}
          (saved as leasedType: {leasedType})
        </Typography>
        {formInputData.map((el) => renderField(el))}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  if (embedded) {
    if (!addData) return null;
    return list();
  }

  return (
    <Drawer
      sx={{
        ...(drawerZIndex ? { zIndex: drawerZIndex } : {}),
        backgroundColor: "transparent",
        height: "100vh",
        minHeight: "100vh",
        overflowY: "auto",
        "& .MuiDrawer-paper": {
          ...(drawerZIndex ? { zIndex: drawerZIndex } : {}),
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleClose}
    >
      {list()}
    </Drawer>
  );
}

export default AddLeasedAssets;
